import axios from 'axios';
import type {
  Cobranca,
  FormaBaixa,
  MetodoCobranca,
  StatusCobranca,
} from '@/api/endpoints/cobrancas';
import { linkWhatsApp } from '@/utils/telefone';

export const STATUS_INFO: Record<
  StatusCobranca,
  { rotulo: string; tom: 'sucesso' | 'erro' | 'aviso' | 'info' | 'neutro' }
> = {
  PENDENTE: { rotulo: 'Aguardando', tom: 'aviso' },
  PAGA: { rotulo: 'Paga', tom: 'sucesso' },
  EXPIRADA: { rotulo: 'Expirada', tom: 'neutro' },
  CANCELADA: { rotulo: 'Cancelada', tom: 'neutro' },
  REEMBOLSADA: { rotulo: 'Reembolsada', tom: 'info' },
  FALHOU: { rotulo: 'Falhou', tom: 'erro' },
};

export const METODO_ROTULO: Record<MetodoCobranca, string> = {
  PIX: 'PIX',
  BOLETO: 'Boleto',
  CARTAO: 'Cartão',
};

// Cobrança aberta sem forma fixa (ou de cartão) é um link: o cliente escolhe.
export const ehLink = (c: Cobranca) =>
  c.status === 'PENDENTE' && (c.metodo === null || c.metodo === 'CARTAO');

export const rotuloForma = (c: Cobranca) =>
  ehLink(c) ? 'Link' : METODO_ROTULO[c.metodo ?? 'CARTAO'];

export const reaisDeCentavos = (centavos: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(centavos / 100);

// "1.234,56", "1234.56" ou "150" → centavos. Inválido → null.
export function centavosDeTexto(texto: string): number | null {
  const limpo = texto.replace(/[^\d,.]/g, '');
  if (!limpo) return null;
  const temVirgula = limpo.includes(',');
  const normalizado = temVirgula ? limpo.replace(/\./g, '').replace(',', '.') : limpo;
  const valor = Number(normalizado);
  if (!Number.isFinite(valor)) return null;
  return Math.round(valor * 100);
}

export const dataHora = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '';

export const dataCurta = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : '';

// Só renderiza como link o que é https: um javascript: vindo de qualquer
// lugar nunca vira href.
export const urlSegura = (url: string | null | undefined): string | null =>
  url && /^https:\/\//i.test(url) ? url : null;

// O QR da AbacatePay já vem como data URL; aceita base64 puro também.
export const srcDoQrCode = (base64: string | null | undefined): string | null => {
  if (!base64) return null;
  if (base64.startsWith('data:image/')) return base64;
  return /^[A-Za-z0-9+/=]+$/.test(base64) ? `data:image/png;base64,${base64}` : null;
};

export const nomeDoCliente = (c: Cobranca) => c.produtor_nome || c.cliente_nome || 'Cliente avulso';

export const descricaoDaCobranca = (c: Cobranca) => {
  if (c.plano_nome) return `${c.plano_nome} · ${c.ciclo === 'anual' ? 'anual' : 'mensal'}`;
  return c.descricao || 'Valor avulso';
};

// Texto pronto para o WhatsApp do cliente, sem emoji e sem travessão. Leva o
// link da página de pagamento, onde ele escolhe Pix, cartão ou boleto.
export function textoParaWhatsApp(c: Cobranca): string {
  const valor = reaisDeCentavos(c.valor_centavos);
  const sobre = c.plano_nome
    ? `do plano ${descricaoDaCobranca(c)} da Curral Tech`
    : 'da Curral Tech';
  const linhas = [`Olá! Segue o link para pagar a cobrança ${sobre}, no valor de ${valor}.`];
  const link = urlSegura(c.link_pagamento);
  if (link) linhas.push('', link, '', 'Dá para pagar com Pix, cartão ou boleto.');
  else if (c.pix_copia_cola) linhas.push('', 'Pix copia e cola:', c.pix_copia_cola);
  linhas.push('Assim que o pagamento cair, o plano é liberado automaticamente.');
  return linhas.join('\n');
}

export function linkWhatsAppDaCobranca(c: Cobranca): string | null {
  const base = c.cliente_telefone ? linkWhatsApp(c.cliente_telefone) : null;
  return base ? `${base}?text=${encodeURIComponent(textoParaWhatsApp(c))}` : null;
}

export function mensagemDeErro(error: unknown, padrao: string): string {
  if (axios.isAxiosError(error)) {
    const msg = (error.response?.data as { message?: string } | undefined)?.message;
    if (msg) return msg;
    if (!error.response) return 'Sem conexão com a API. Tente de novo.';
  }
  return padrao;
}

export const FORMA_BAIXA_ROTULO: Record<FormaBaixa, string> = {
  pix_direto: 'Pix direto',
  dinheiro: 'Dinheiro',
  transferencia: 'Transferência',
  outro: 'Outro',
};
